import React, { useState, useRef, useCallback, useEffect } from 'react';

import i18n from 'i18n-js';
import { useSelector, useDispatch } from 'react-redux';

import { Table } from 'components/UI';
import { FETCH_STATE } from 'constants';
import { debounce } from 'lodash';
import { vehicleActions } from 'state/modules/vehicles';
import { useHistory } from 'react-router-dom';
import { buildUrl } from 'utils';

export const VehicleDetails = ({ invoiceItemId }) => {
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    invoice_item_id: invoiceItemId,
  });

  const isMounted = useRef(false);
  const abortController = useRef(null);
  const fetchingVehicles = useSelector((state) => state.vehicles?.fetchingVehicles);
  const vehicles = useSelector((state) => state.vehicles?.vehicles);
  const pagination = useSelector((state) => state.vehicles?.pagination);
  const currentOrganization = useSelector((state) => state.auth?.currentOrganization);
  const isOwner = currentOrganization?.organization_type === 'OWNER';
  const history = useHistory();

  const dispatch = useDispatch();

  const loading = fetchingVehicles === FETCH_STATE.LOADING;

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      sortable: true,
      grow: 0,
      minWidth: '4rem',
    },
    {
      name: i18n.t('common.name'),
      selector: 'name',
      cell: (row) => row?.name || '--',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('common.type'),
      selector: (row) =>
        `${row?.vehicle_type?.make ? `${row?.vehicle_type?.make} ` : ''}${
          row?.vehicle_type?.model || ''
        }` || '--',
      grow: 2,
    },
    {
      name: i18n.t('vehicle.license_plate'),
      selector: (row) => row?.license_plate || '--',
      grow: 2,
    },
    {
      name: i18n.t('vehicle.vin'),
      selector: 'vin',
      cell: (row) => row?.vin || '--',
      sortable: true,
      grow: 2,
    },
    {
      name: isOwner ? i18n.t('common.fleet') : i18n.t('common.vehicle_group'),
      selector: 'vehicle_group',
      grow: 3,
      cell: (row) => row?.vehicle_group?.name || '--',
    },
    {
      name: i18n.t('common.status'),
      selector: (row) =>
        row?.status ? i18n.t(`status.${row?.status}`, { defaultValue: row?.status }) : '--',
    },
  ];

  const handleParams = (newParams) => {
    setParams({ ...newParams });
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      page: pagination?.currentPage,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handleRowClick = (row) => {
    history.push(buildUrl(`/vehicles/${row?.id}`));
  };

  const handlePerRowsChange = useCallback((limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  }, []);

  const handlePageChange = useCallback((page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  }, []);

  const fetchVehicles = useCallback(
    (params) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(vehicleActions.fetchVehicles(params, { signal: abortController?.current?.signal }));
    },
    [dispatch]
  );

  const fetchVehiclesDebounced = useCallback(
    debounce((params) => {
      fetchVehicles(params);
    }, 1000),
    [fetchVehicles]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchVehiclesDebounced(params);
    }
  }, [fetchVehiclesDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchVehicles(params);
    isMounted.current = true;
  }, []);

  return (
    <Table
      data={vehicles}
      columns={columns}
      page={params.page}
      onSort={handleSort}
      limit={params.limit}
      loading={loading}
      handleRowClicked={handleRowClick}
      handlePageChange={handlePageChange}
      defaultSortAsc={!params?.sort_desc}
      defaultSortField={params?.sort_field}
      totalRows={pagination?.totalRows || 0}
      handlePerRowsChange={handlePerRowsChange}
      paginationDefaultPage={pagination?.currentPage}
    />
  );
};
